import { createContext, useCallback, useContext, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { putChunk } from "@/hooks/useUploader";

const Ctx = createContext(null);

// Lives above the router so zip uploads keep going while the user browses other pages.
export const TakeoutProvider = ({ children }) => {
  const [uploads, setUploads] = useState({});
  const queue = useRef(Promise.resolve());
  const patch = useCallback((key, p) => setUploads((u) => ({ ...u, [key]: { ...u[key], ...p } })), []);

  const uploadZip = useCallback(async (jobId, file) => {
    const key = `${jobId}:${file.name}:${file.size}`;
    patch(key, { jobId, name: file.name, progress: 0, state: "uploading", error: null });
    try {
      const { data } = await api.post("/uploads/init", { filename: file.name, size: file.size, space: "private", kind: "takeout", job_id: jobId });
      let offset = data.received || 0;
      while (offset < file.size) {
        const blob = file.slice(offset, offset + data.chunk_size);
        const base = offset;
        await putChunk(data.upload_id, blob, offset, (loaded) => patch(key, { progress: Math.min(99.5, ((base + loaded) / file.size) * 100) }));
        offset += blob.size;
      }
      await api.post(`/uploads/${data.upload_id}/complete`);
      patch(key, { progress: 100, state: "done" });
    } catch (e) {
      patch(key, { state: "failed", error: errMsg(e) });
    }
  }, [patch]);

  const start = useCallback((jobId, files) => {
    Array.from(files).forEach((f) => {
      queue.current = queue.current.then(() => uploadZip(jobId, f));
    });
  }, [uploadZip]);

  return <Ctx.Provider value={{ uploads, start }}>{children}</Ctx.Provider>;
};

export const useTakeout = () => useContext(Ctx);
